import React from 'react';
import { useTranslation } from 'react-i18next';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	InputAdornment,
	TextField,
} from '@mui/material';

import { bookingLessonName, parseDate, teacherName } from '@common/helpers/academyReworkings';
import { BookedMeet } from '@common/types/academyReworkings';

import { nextButtonSx, twoColumnsSx } from '../../styled';
import detailFieldSx from './styled';

function DetailField({
	label,
	value,
	href,
}: {
	label: string;
	value?: string | null;
	href?: string;
}) {
	return (
		<TextField
			fullWidth
			size="small"
			label={label}
			value={value || '—'}
			InputProps={{
				inputProps: { title: value || '—' },
				readOnly: true,
				endAdornment: href ? (
					<InputAdornment position="end">
						<IconButton
							component="a"
							href={href}
							target="_blank"
							rel="noopener noreferrer"
							aria-label={label}
							sx={{ width: '28px', height: '28px', p: 0 }}
						>
							<OpenInNewIcon sx={{ fontSize: '18px' }} />
						</IconButton>
					</InputAdornment>
				) : undefined,
			}}
			sx={detailFieldSx}
		/>
	);
}

function DetailLink({ label, value }: { label: string; value?: string | null }) {
	let href: string | undefined;
	if (value) {
		try {
			if (['https:', 'http:'].includes(new URL(value).protocol)) href = value;
		} catch {
			href = undefined;
		}
	}
	return <DetailField label={label} value={value} href={href} />;
}

export default function BookingDetails({
	booking,
	onClose,
}: {
	booking: BookedMeet | null;
	onClose: () => void;
}) {
	const { t } = useTranslation();
	return (
		<Dialog
			open={!!booking}
			onClose={onClose}
			fullWidth
			maxWidth="sm"
			aria-labelledby="academy-booking-details-title"
			PaperProps={{
				sx: {
					maxWidth: '640px',
					width: 'calc(100% - 32px)',
					p: 2,
					m: 2,
					minWidth: 0,
					overflowWrap: 'anywhere',
				},
			}}
		>
			<DialogTitle id="academy-booking-details-title">
				{t('academyReworkings.bookingDetails')}
			</DialogTitle>
			<DialogContent sx={{ minWidth: 0 }}>
				{booking && (
					<Box display="flex" flexDirection="column" gap="16px" pt={1}>
						<Box sx={twoColumnsSx}>
							<DetailField
								label={t('academyReworkings.bookingDate')}
								value={parseDate(booking.start_dt).toFormat('ccc, dd.MM.yyyy')}
							/>
							<DetailField
								label={t('academyReworkings.time')}
								value={`${parseDate(booking.start_dt).toFormat('HH:mm')}–${parseDate(booking.end_dt).toFormat('HH:mm')}`}
							/>
						</Box>
						<DetailField
							label={t('academyReworkings.program')}
							value={booking.programGroup?.program?.name}
						/>
						<DetailField
							label={t('academyReworkings.lesson')}
							value={bookingLessonName(booking, t('academyReworkings.main'))}
						/>
						<DetailField
							label={t('academyReworkings.tutor')}
							value={booking.teacher ? teacherName(booking.teacher) : undefined}
						/>
						<DetailField label={t('academyReworkings.phone')} value={booking.phone} />
						<Box sx={twoColumnsSx}>
							<DetailField label={t('academyReworkings.zoomId')} value={booking.zoom_id} />
							<DetailField
								label={t('academyReworkings.zoomAccessCode')}
								value={booking.zoom_access_code}
							/>
						</Box>
						<DetailLink label={t('academyReworkings.zoomLink')} value={booking.zoom_link} />
						<DetailLink label={t('academyReworkings.miroLink')} value={booking.miro_link} />
						{booking.comment_teacher && (
							<DetailField
								label={t('academyReworkings.teacherComment')}
								value={booking.comment_teacher}
							/>
						)}
						{booking.comment_student && (
							<DetailField
								label={t('academyReworkings.studentComment')}
								value={booking.comment_student}
							/>
						)}
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button variant="contained" sx={nextButtonSx} onClick={onClose}>
					{t('academyReworkings.closeDetails')}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
